import {
  ChevronDown,
  DollarSign,
  Eye,
  MousePointer,
  Percent,
  Search,
  Target,
  TrendingUp,
} from 'lucide-react'
import { useState } from 'react'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import {
  googleAdsGetSearchQueryDetails,
  type GoogleAdsGetSearchQueryDetailsResult,
} from 'sprites-types/workflow/tools/googleAdsGetSearchQueryDetails'
import { ObjectDisplay } from '../shared/ObjectDisplay'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

type SearchQueryItem = GoogleAdsGetSearchQueryDetailsResult[number]

// Utility functions
const formatCurrency = (amount: number | null | undefined, currency: string = 'USD') => {
  if (amount == null) return 'N/A'
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency }).format(amount)
}

const formatNumber = (num: number | null | undefined) => {
  if (num == null) return 'N/A'
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`
  return num.toLocaleString()
}

const formatPercent = (value: number | null | undefined) => {
  if (value == null) return 'N/A'
  return `${value.toFixed(2)}%`
}

const formatROAS = (roas: number | null | undefined) => {
  if (roas == null) return 'N/A'
  return `${roas.toFixed(2)}x`
}

function SearchQueryItem({ queryItem }: { queryItem: SearchQueryItem }) {
  const [isExpanded, setIsExpanded] = useState(false)
  const metrics = queryItem.metrics || {}
  const currencyCode = queryItem.currencyCode || 'USD'

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="mb-2 flex items-center gap-2">
            <Search className="h-4 w-4 text-blue-600" />
            <h4 className="break-words font-medium text-gray-900">{queryItem.query}</h4>
          </div>
          {(queryItem.campaign || queryItem.adGroup) && (
            <p className="text-sm text-gray-600">
              {queryItem.campaign && `Campaign: ${queryItem.campaign}`}
              {queryItem.campaign && queryItem.adGroup && ' • '}
              {queryItem.adGroup && `Ad Group: ${queryItem.adGroup}`}
            </p>
          )}
        </div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="ml-2 text-gray-400 hover:text-gray-600"
        >
          <ChevronDown
            className={`h-5 w-5 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          />
        </button>
      </div>

      {/* Performance Metrics Grid */}
      <div className="mt-3 grid grid-cols-3 gap-3">
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-2">
          <div className="mb-1 flex items-center gap-1">
            <Eye className="h-3 w-3 text-gray-600" />
            <span className="text-xs font-medium text-gray-700">Impressions</span>
          </div>
          <div className="text-base font-bold text-gray-900">
            {formatNumber(metrics.impressions)}
          </div>
        </div>

        {metrics.clicks != null && metrics.clicks > 0 && (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-2">
            <div className="mb-1 flex items-center gap-1">
              <MousePointer className="h-3 w-3 text-gray-600" />
              <span className="text-xs font-medium text-gray-700">Clicks</span>
            </div>
            <div className="text-base font-bold text-gray-900">{formatNumber(metrics.clicks)}</div>
          </div>
        )}

        {metrics.cost != null && metrics.cost > 0 && (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-2">
            <div className="mb-1 flex items-center gap-1">
              <DollarSign className="h-3 w-3 text-gray-600" />
              <span className="text-xs font-medium text-gray-700">Cost</span>
            </div>
            <div className="text-base font-bold text-gray-900">
              {formatCurrency(metrics.cost, currencyCode)}
            </div>
          </div>
        )}
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="mt-3 border-t border-gray-200 pt-3">
          <div className="grid grid-cols-2 gap-3">
            {metrics.ctr != null && metrics.ctr > 0 && (
              <div className="text-sm">
                <span className="font-medium text-gray-700">CTR: </span>
                <span className="text-gray-900">{formatPercent(metrics.ctr)}</span>
              </div>
            )}
            {metrics.averageCpc != null && metrics.averageCpc > 0 && (
              <div className="text-sm">
                <span className="font-medium text-gray-700">CPC: </span>
                <span className="text-gray-900">
                  {formatCurrency(metrics.averageCpc, currencyCode)}
                </span>
              </div>
            )}
            {metrics.conversions != null && metrics.conversions > 0 && (
              <div className="text-sm">
                <span className="font-medium text-gray-700">Conversions: </span>
                <span className="text-gray-900">{formatNumber(metrics.conversions)}</span>
              </div>
            )}
            {metrics.conversionValue != null && metrics.conversionValue > 0 && (
              <div className="text-sm">
                <span className="font-medium text-gray-700">Conversion Value: </span>
                <span className="text-gray-900">
                  {formatCurrency(metrics.conversionValue, currencyCode)}
                </span>
              </div>
            )}
            {metrics.valuePerConversion != null && metrics.valuePerConversion > 0 && (
              <div className="text-sm">
                <span className="font-medium text-gray-700">Value per Conversion: </span>
                <span className="text-gray-900">
                  {formatCurrency(metrics.valuePerConversion, currencyCode)}
                </span>
              </div>
            )}
            {metrics.roas != null && metrics.roas > 0 && (
              <div className="text-sm">
                <span className="font-medium text-gray-700">ROAS: </span>
                <span className="text-gray-900">{formatROAS(metrics.roas)}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function GoogleAdsGetSearchQueryDetailsDisplayComponent({
  nodeExecution,
}: WorkflowToolComponentProps) {
  const [showJson, setShowJson] = useState(false)
  const schema = getToolResultSchema(googleAdsGetSearchQueryDetails, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data as GoogleAdsGetSearchQueryDetailsResult

  if (showJson) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">Search Query Details Results</h3>
          <button
            onClick={() => setShowJson(false)}
            className="text-xs text-blue-600 hover:text-blue-800"
          >
            View Formatted
          </button>
        </div>
        <ObjectDisplay data={result} />
      </div>
    )
  }

  if (!result || result.length === 0) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <h3 className="text-sm font-semibold text-gray-900">Search Query Details Results</h3>
        <p className="mt-2 text-sm text-gray-600">No search query performance data found.</p>
      </div>
    )
  }

  // Calculate totals
  const totals = result.reduce(
    (acc, queryItem) => {
      const metrics = queryItem.metrics || {}
      acc.impressions += metrics.impressions || 0
      acc.clicks += metrics.clicks || 0
      acc.cost += metrics.cost || 0
      acc.conversions += metrics.conversions || 0
      acc.conversionValue += metrics.conversionValue || 0
      return acc
    },
    { impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionValue: 0 }
  )

  const avgCTR = totals.impressions > 0 ? (totals.clicks / totals.impressions) * 100 : 0
  const totalROAS =
    totals.cost > 0 && totals.conversionValue > 0 ? totals.conversionValue / totals.cost : null
  const currencyCode = result[0]?.currencyCode || 'USD'

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Search Query Details Results</h3>
          <p className="text-xs text-gray-600">
            {result.length} search quer{result.length !== 1 ? 'ies' : 'y'}
          </p>
        </div>
        <button
          onClick={() => setShowJson(true)}
          className="text-xs text-blue-600 hover:text-blue-800"
        >
          View JSON
        </button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-4 gap-3">
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
          <div className="mb-1 flex items-center gap-1">
            <Eye className="h-3 w-3 text-gray-600" />
            <span className="text-xs font-medium text-gray-700">Total Impressions</span>
          </div>
          <div className="text-lg font-bold text-gray-900">{formatNumber(totals.impressions)}</div>
        </div>
        {totals.clicks > 0 && (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
            <div className="mb-1 flex items-center gap-1">
              <MousePointer className="h-3 w-3 text-gray-600" />
              <span className="text-xs font-medium text-gray-700">Total Clicks</span>
            </div>
            <div className="text-lg font-bold text-gray-900">{formatNumber(totals.clicks)}</div>
          </div>
        )}
        {totals.cost > 0 && (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
            <div className="mb-1 flex items-center gap-1">
              <DollarSign className="h-3 w-3 text-gray-600" />
              <span className="text-xs font-medium text-gray-700">Total Cost</span>
            </div>
            <div className="text-lg font-bold text-gray-900">
              {formatCurrency(totals.cost, currencyCode)}
            </div>
          </div>
        )}
        {avgCTR > 0 && (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
            <div className="mb-1 flex items-center gap-1">
              <Percent className="h-3 w-3 text-gray-600" />
              <span className="text-xs font-medium text-gray-700">Avg CTR</span>
            </div>
            <div className="text-lg font-bold text-gray-900">{formatPercent(avgCTR)}</div>
          </div>
        )}
      </div>

      {/* Additional Summary Stats */}
      {(totals.conversions > 0 || totalROAS) && (
        <div className="grid grid-cols-2 gap-3">
          {totals.conversions > 0 && (
            <div className="rounded-lg border border-gray-200 bg-green-50 p-3">
              <div className="mb-1 flex items-center gap-1">
                <TrendingUp className="h-3 w-3 text-green-600" />
                <span className="text-xs font-medium text-green-700">Total Conversions</span>
              </div>
              <div className="text-lg font-bold text-green-900">
                {formatNumber(totals.conversions)}
              </div>
            </div>
          )}
          {totalROAS && (
            <div className="rounded-lg border border-gray-200 bg-blue-50 p-3">
              <div className="mb-1 flex items-center gap-1">
                <Target className="h-3 w-3 text-blue-600" />
                <span className="text-xs font-medium text-blue-700">Total ROAS</span>
              </div>
              <div className="text-lg font-bold text-blue-900">{formatROAS(totalROAS)}</div>
            </div>
          )}
        </div>
      )}

      {/* Search Queries List */}
      <div className="space-y-3">
        {result.map((queryItem, index) => (
          <SearchQueryItem key={queryItem.query || index} queryItem={queryItem} />
        ))}
      </div>
    </div>
  )
}

async function downloadGoogleAdsSearchQueryPerformance(
  _nodeExecution: NodeExecution
): Promise<void> {
  return Promise.resolve()
}

function googleAdsGetSearchQueryDetailsToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(googleAdsGetSearchQueryDetails, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const result = parseResult.data
  return `## Google Ads Search Query Details\n\n\`\`\`json\n${JSON.stringify(result, null, 2)}\n\`\`\`\n\n---`
}

export const GoogleAdsGetSearchQueryDetailsDisplay = createWorkflowToolComponent(
  GoogleAdsGetSearchQueryDetailsDisplayComponent,
  downloadGoogleAdsSearchQueryPerformance,
  googleAdsGetSearchQueryDetailsToMarkdown
)
